/* eslint-disable react/jsx-no-bind */
// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@locus-taxy/neo-ui';

import { MultiSelectFilter, SelectTypes } from 'common/components/core/select';
import { RetrySnackbar } from 'common/components/core/snackbar';

import useRoles from 'common/hooks/useRoles';
import { ApiStatusEnum } from 'common/enums/API';
import { ApiError } from 'common/modules/api';
import useClientId from 'common/hooks/useClientId';

type Value = { values: Array<string>, allowEmptyOrNull: boolean };

type Props = {
    value: ?Value,
    onChange: (value: ?Value) => void,
    disabled?: ?boolean,
    className?: ?string
};

const PersonnelMasterRoleFilter = ({
    value,
    onChange,
    disabled,
    className
}: Props): React.Node => {
    const { t } = useTranslation();
    const clientId = useClientId();
    const rolesQuery = useRoles({ clientId, includeDisabled: true });

    const { data: roles = [] } = rolesQuery;

    const options = React.useMemo(() => {
        return roles.map(role => ({
            label: role.name,
            value: role.id
        }));
    }, [roles]);

    if (rolesQuery.status === ApiStatusEnum.Loading) {
        return <Loader width="100%" />;
    }

    if (rolesQuery.status === ApiStatusEnum.Error) {
        return (
            <RetrySnackbar
                errorMessage={
                    rolesQuery.error instanceof ApiError
                        ? rolesQuery.error.message
                        : t('genericError')
                }
                active
                onClick={rolesQuery.refetch}
            />
        );
    }

    const handleChange = ({ values: options, allowEmptyOrNull }) => {
        // adding logic for multiselect and special handling for (BLANKS) case.
        const values = options.map(option => option.value);
        // on clear selection
        if (!values.length && !allowEmptyOrNull) {
            onChange();
            return;
        }

        onChange({ values, allowEmptyOrNull });
    };

    return (
        <MultiSelectFilter
            className={className}
            value={value?.values || []}
            onChange={handleChange}
            allowEmptyOrNull={value?.allowEmptyOrNull || false}
            options={options}
            selectType={SelectTypes.Outline}
            disabled={disabled}
        />
    );
};

export default PersonnelMasterRoleFilter;
